import { ClickToCopyRemotePromoId } from "@/components/click_to_copy_remote_promo_id.tsx"
import { Edit3, Calendar, Tag, MapPin, AlertCircle, Trash2 } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { Promotion } from '../../promo_creation/types'
import { exactCurrencyFormatter } from '../../../../utils'

interface PromotionDisplayProps {
  promotion: Promotion
  onEdit: () => void
  onDelete?: () => void
}

export function PromotionDisplay({ promotion, onEdit, onDelete }: PromotionDisplayProps) {
  return (
    <div class="border border-gray-200 rounded-lg p-4 bg-white">
      <div class="flex justify-between items-start mb-3">
        <div>
          <h4 class="font-medium text-gray-900">{promotion.name}</h4>
          {promotion.remote_promo_id && (
            <div class="mt-1 inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-800">
              Promo ID:{" "}
              <ClickToCopyRemotePromoId
                copyValue={promotion.remote_promo_id}
                className="font-semibold text-indigo-800 hover:underline"
              >
                {promotion.remote_promo_id}
              </ClickToCopyRemotePromoId>
            </div>
          )}
        </div>
        <div class="flex gap-2">
          <Button onClick={onEdit} variant="outline" class="h-10 px-3">
            <Edit3 className="h-5 w-5 mr-2" />
            Edit or View Details
          </Button>
          {onDelete && (
            <Button onClick={onDelete} variant="outline" class="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {promotion.description && (
        <p class="text-sm text-gray-600 mb-3">{promotion.description}</p>
      )}
      
      {/* Header row: two-column grid for date and type+discount */}
      <div class="grid grid-cols-2 items-center text-sm my-4">
        {/* Date on the left */}
        <div class="flex items-center gap-2">
          {promotion.start_date && promotion.end_date && (
            <>
              <Calendar className="h-4 w-4 text-gray-400" />
              <span>{promotion.start_date} - {promotion.end_date}</span>
            </>
          )}
        </div>
        
        {/* Type + discount centered in the second column */}
        <div class="flex items-center gap-2 justify-center">
          {promotion.promotion_type && (
            <>
              <Tag className="h-4 w-4 text-gray-400" />
              <span>{promotion.promotion_type}</span>
            </>
          )}
          {(promotion.discount_percentage || promotion.discount_amount || (promotion.multibuy_quantity && promotion.multibuy_price)) && (
            <span class="font-medium">
              {promotion.multibuy_quantity && promotion.multibuy_price 
                ? `${promotion.multibuy_quantity} for ${exactCurrencyFormatter(promotion.multibuy_price)}`
                : promotion.discount_percentage 
                  ? `${promotion.discount_percentage}%` 
                  : promotion.discount_amount 
                    ? exactCurrencyFormatter(promotion.discount_amount)
                    : null}
            </span>
          )}
        </div>
      </div>
      
      {/* Products */}
      {promotion.products.length > 0 && (
        <div class="mt-3">
          <h5 class="text-sm font-medium text-gray-700 mb-1">Products:</h5>
          <div class="flex flex-wrap gap-1">
            {promotion.products.map((product, index) => (
              <span key={index} class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                {product}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Customer and Retailer */}
      {(promotion.customer || promotion.retailer) && (
        <div class="mt-3">
          <h5 class="text-sm font-medium text-gray-700 mb-1">Distribution:</h5>
          <div class="flex flex-wrap gap-2">
            {promotion.customer && (
              <span class="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                Customer: {promotion.customer}
              </span>
            )}
            {promotion.retailer && (
              <span class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                Retailer: {promotion.retailer}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Regions */}
      {promotion.regions && promotion.regions.length > 0 && (
        <div class="mt-3">
          <h5 class="text-sm font-medium text-gray-700 mb-1">Regions:</h5>
          <div class="flex flex-wrap gap-1">
            {promotion.regions.map((region, index) => (
              <span key={index} class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {region}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Terms */}
      {promotion.terms && promotion.terms.length > 0 && (
        <div class="mt-3">
          <h5 class="text-sm font-medium text-gray-700 mb-1">Terms:</h5>
          <ul class="text-sm text-gray-600 space-y-1">
            {promotion.terms.map((term, index) => (
              <li key={index} class="flex items-start">
                <span class="mr-2">•</span>
                {term}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Restrictions */}
      {promotion.restrictions && promotion.restrictions.length > 0 && (
        <div class="mt-3">
          <h5 class="text-sm font-medium text-gray-700 mb-1">Restrictions:</h5>
          <ul class="text-sm text-gray-600 space-y-1">
            {promotion.restrictions.map((restriction, index) => (
              <li key={index} class="flex items-start">
                <AlertCircle className="h-4 w-4 text-red-400 mr-2 mt-0.5 shrink-0" />
                {restriction}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
